import React, { useState, useEffect, useRef, useMemo } from 'react';
import { formatFundingPnL, getFundingPct } from '../utils/stats';
import './PositionsTable.css';
import ADLTooltip from './ADLTooltip';

function PositionsTable({ positions, hedgingAnalysis, fundingNetByMarket = [] }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [liveFRDisplay, setLiveFRDisplay] = useState('raw'); // 'raw' | 'daily' | 'weekly' | 'yearly' — how to show live funding rate
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const [hedgeFilter, setHedgeFilter] = useState('all'); // 'all', 'yes', 'no'
  const columnMenuRef = useRef(null);
  
  // Column visibility state - default all visible
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('positionsTableColumnVisibility');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        parsed.fundingPct = false; // Always hide Funding % on dashboard
        return parsed;
      } catch (e) {
        // If parsing fails, use defaults
      }
    }
    return {
      symbol: true,
      exchange: true,
      side: true,
      quantity: true,
      avgOpen: true,
      markPrice: true,
      liqPrice: true,
      adl: true,
      unrealizedPnl: true,
      gain24h: true,
      notional: true,
      margin: true,
      leverage: true,
      liveFR: true,
      funding: true,
      fundingPct: false,
      countdown: true,
      hedging: true,
    };
  });
  
  // Update current time every second for countdown timers
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Save column visibility to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('positionsTableColumnVisibility', JSON.stringify(columnVisibility));
  }, [columnVisibility]);

  // Close column menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(event.target)) {
        setShowColumnMenu(false);
      }
    };

    if (showColumnMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColumnMenu]);

  // Toggle column visibility
  const toggleColumn = (columnKey) => {
    setColumnVisibility(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  // Column definitions with labels
  const columns = [
    { key: 'symbol', label: 'Symbol' },
    { key: 'exchange', label: 'Exchange' },
    { key: 'side', label: 'Side' },
    { key: 'quantity', label: 'Quantity' },
    { key: 'avgOpen', label: 'Avg. Open' },
    { key: 'markPrice', label: 'Mark Price' },
    { key: 'gain24h', label: '24h Gain' },
    { key: 'liqPrice', label: 'Liq. Price' },
    { key: 'adl', label: 'ADL' },
    { key: 'unrealizedPnl', label: 'U-PnL (ROE%)' },
    { key: 'notional', label: 'Notional' },
    { key: 'margin', label: 'Margin' },
    { key: 'leverage', label: 'Leverage' },
    { key: 'liveFR', label: 'Live FR' },
    { key: 'funding', label: 'Funding' },
    { key: 'fundingPct', label: 'Funding %' },
    { key: 'countdown', label: 'Countdown' },
    { key: 'hedging', label: 'Hedged' },
  ];

  // Combine all positions from all exchanges
  // Safely handle cases where positions might be undefined or not an array
  const binancePos = (positions?.binance?.positions && Array.isArray(positions.binance.positions)) 
    ? positions.binance.positions 
    : [];
  const bybitPos = (positions?.bybit?.positions && Array.isArray(positions.bybit.positions))
    ? positions.bybit.positions
    : [];
  const okxPos = (positions?.okx?.positions && Array.isArray(positions.okx.positions))
    ? positions.okx.positions
    : [];
  const hyperliquidPos = (positions?.hyperliquid?.positions && Array.isArray(positions.hyperliquid.positions))
    ? positions.hyperliquid.positions
    : [];
  const lighterPos = (positions?.lighter?.positions && Array.isArray(positions.lighter.positions))
    ? positions.lighter.positions
    : [];
  const extendedPos = (positions?.extended?.positions && Array.isArray(positions.extended.positions))
    ? positions.extended.positions
    : [];
  
  const allPositions = [
    ...binancePos.map(p => ({ ...p, account: 'Binance' })),
    ...bybitPos.map(p => ({ ...p, account: 'Bybit' })),
    ...okxPos.map(p => ({ ...p, account: 'OKX' })),
    ...hyperliquidPos.map(p => ({ ...p, account: 'Hyperliquid' })),
    ...lighterPos.map(p => ({ ...p, account: 'Lighter' })),
    ...extendedPos.map(p => ({ ...p, account: 'Extended' }))
  ];

  // Debug logging
  React.useEffect(() => {
  }, [positions, allPositions.length]);

  // Get hedging info for each position
  const getHedgingInfo = (position) => {
    const analysis = hedgingAnalysis?.find(a => 
      (a.binance_position?.pair === position.pair && position.exchange === 'binance') ||
      (a.hyperliquid_position?.pair === position.pair && position.exchange === 'hyperliquid') ||
      (a.lighter_position?.pair === position.pair && position.exchange === 'lighter') ||
      (a.extended_position?.pair === position.pair && position.exchange === 'extended')
    );
    return analysis;
  };

  // Check if position is hedged
  const isPositionHedged = (position) => {
    const hedgingInfo = getHedgingInfo(position);
    if (!hedgingInfo) return false;
    return hedgingInfo.status === 'fully_hedged' || hedgingInfo.status === 'partially_hedged';
  };

  // Filter positions based on search query and hedging filter
  let filteredPositions = allPositions.filter(position => {
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = (
        position.pair.toLowerCase().includes(query) ||
        position.account.toLowerCase().includes(query) ||
        position.side.toLowerCase().includes(query)
      );
      if (!matchesSearch) return false;
    }

    // Hedging filter
    if (hedgeFilter === 'yes') {
      return isPositionHedged(position);
    } else if (hedgeFilter === 'no') {
      return !isPositionHedged(position);
    }
    
    return true; // 'all' - show all positions
  });

  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return '-';
    return num.toFixed(decimals);
  };

  const formatPrice = (num) => {
    if (num === null || num === undefined) return '-';
    // Use 3 decimals if price is below 1, otherwise 2 decimals
    const decimals = num < 1 ? 3 : 2;
    return num.toFixed(decimals);
  };

  const formatLargeNumber = (num) => {
    if (num === null || num === undefined) return '-';
    if (num >= 1000) {
      return num.toLocaleString('en-US', { maximumFractionDigits: 2 });
    }
    return num.toFixed(2);
  };

  const formatCountdown = (position) => {
    if (!position.funding_rate_info) return '-';
    
    // Always calculate from next_funding_time if available (most accurate)
    if (position.funding_rate_info.next_funding_time) {
      try {
        // Parse the next funding time (handles ISO format from API)
        const nextFundingStr = position.funding_rate_info.next_funding_time;
        
        // Ensure UTC timezone if not specified (API sends UTC but without 'Z')
        let nextFunding;
        if (nextFundingStr.includes('Z') || nextFundingStr.includes('+') || nextFundingStr.includes('-', 10)) {
          // Already has timezone info
          nextFunding = new Date(nextFundingStr);
        } else {
          // No timezone - treat as UTC (API sends UTC times)
          nextFunding = new Date(nextFundingStr + 'Z');
        }
        
        const now = new Date(currentTime);
        const diff = Math.max(0, Math.floor((nextFunding - now) / 1000));
        
        // Check if date is valid
        if (isNaN(nextFunding.getTime())) {
          console.warn(`Invalid next_funding_time for ${position.pair}:`, nextFundingStr);
          // Fallback to countdown_seconds if available
          const countdownSeconds = position.funding_rate_info.countdown_seconds;
          if (countdownSeconds !== null && countdownSeconds !== undefined && countdownSeconds > 0) {
            return formatTime(Math.max(0, countdownSeconds));
          }
          return '-';
        }
        
        return formatTime(diff);
      } catch (e) {
        console.error(`Error parsing next_funding_time for ${position.pair}:`, e, position.funding_rate_info);
        // Fallback to countdown_seconds
        const countdownSeconds = position.funding_rate_info.countdown_seconds;
        if (countdownSeconds !== null && countdownSeconds !== undefined && countdownSeconds > 0) {
          return formatTime(Math.max(0, countdownSeconds));
        }
        return '-';
      }
    }
    
    // Fallback: use countdown_seconds if next_funding_time is not available
    const countdownSeconds = position.funding_rate_info.countdown_seconds;
    if (countdownSeconds !== null && countdownSeconds !== undefined && countdownSeconds > 0) {
      return formatTime(Math.max(0, countdownSeconds));
    }
    
    return '-';
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Funding % since opening and annualized (shared algo: periodPct = net/notional*100, annualizedPct = periodPct * (365/days))
  const getFundingPctForPosition = (position) => {
    const notional = position.notional || (position.size * position.mark_price) || 0;
    return getFundingPct(
      position.cumulative_funding_payout,
      notional,
      position.opening_time
    );
  };

  // Live FR column: show current funding rate as raw (per 8h), daily, weekly, or yearly
  const formatFundingRate = (position) => {
    const info = position.funding_rate_info;
    if (!info || info.current_rate === undefined) return '-';
    const r = info.current_rate; // decimal per 8h
    let value; // as percentage
    if (liveFRDisplay === 'raw') {
      value = r * 100;
    } else if (liveFRDisplay === 'daily') {
      value = r * 3 * 100; // 3 x 8h = 1 day
    } else if (liveFRDisplay === 'weekly') {
      value = r * 21 * 100; // 21 x 8h = 1 week
    } else {
      value = r * 365 * 3 * 100; // yearly
    }
    return (
      <div className={`live-funding-rate ${value >= 0 ? 'positive' : 'negative'}`}>
        {(value >= 0 ? '+' : '') + value.toFixed(liveFRDisplay === 'raw' ? 4 : 2)}%
      </div>
    );
  };

  // Funding % column: both since open and annualized per line
  const formatFundingPct = (position) => {
    const pct = getFundingPctForPosition(position);
    if (pct === null) return '-';
    const sinceCls = pct.periodPct >= 0 ? 'positive' : 'negative';
    const annCls = pct.annualizedPct >= 0 ? 'positive' : 'negative';
    return (
      <div className="funding-pct-cell" title="% since opening (annualised)">
        <span className={sinceCls}>{(pct.periodPct >= 0 ? '+' : '') + pct.periodPct.toFixed(2)}%</span>
        <span className={`funding-pct-ann ${annCls}`}>{(pct.annualizedPct >= 0 ? '+' : '') + pct.annualizedPct.toFixed(1)}% ann.</span>
      </div>
    );
  };

  // Map (exchange, pair) -> net_funding from Funding table (same as History → Funding payments Net FP)
  const netFpByMarketKey = useMemo(() => {
    const map = {};
    fundingNetByMarket.forEach((m) => {
      const ex = (m.exchange || '').toString().toLowerCase();
      const pairNorm = (m.pair || '').toString().toUpperCase().replace(/-/g, '');
      if (ex && pairNorm) map[`${ex}_${pairNorm}`] = m.net_funding;
    });
    return map;
  }, [fundingNetByMarket]);

  // Format funding: use Net FP from Funding table when available, else position cumulative
  const formatFundingPayout = (position) => {
    const ex = (position.exchange || '').toString().toLowerCase();
    const pairNorm = (position.pair || '').toString().toUpperCase().replace(/-/g, '');
    const netFp = ex && pairNorm ? netFpByMarketKey[`${ex}_${pairNorm}`] : undefined;

    if (netFp !== undefined) {
      return (
        <div
          className={`funding-payout ${netFp >= 0 ? 'profit' : 'loss'}`}
          title="Net FP from Funding table (History → Funding payments)"
        >
          {formatFundingPnL(netFp)}
        </div>
      );
    }

    if (position.cumulative_funding_payout === undefined) return '-';

    const payout = position.cumulative_funding_payout;
    const isLighter = position.exchange === 'lighter';
    const isApproximation = isLighter && !position.funding_payout_from_websocket;
    const baseTitle = 'Cumulative funding since position open. $0 = no payments yet or (Lighter/Extended) run History → Refresh.';
    const title = isApproximation ? `Approximation: from funding rate history. ${baseTitle}` : baseTitle;

    return (
      <div
        className={`funding-payout ${payout >= 0 ? 'profit' : 'loss'} ${isApproximation ? 'lighter-approximation' : ''}`}
        title={title}
      >
        {formatFundingPnL(payout)}
      </div>
    );
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortPositions = (positions) => {
    if (!sortConfig.key) return positions;
    
    const sorted = [...positions].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      // Handle nested properties
      if (sortConfig.key === 'pair') {
        aVal = a.pair?.toLowerCase() || '';
        bVal = b.pair?.toLowerCase() || '';
      } else if (sortConfig.key === 'account') {
        aVal = a.account?.toLowerCase() || '';
        bVal = b.account?.toLowerCase() || '';
      } else if (sortConfig.key === 'side') {
        aVal = a.side?.toLowerCase() || '';
        bVal = b.side?.toLowerCase() || '';
      } else if (sortConfig.key === 'unrealized_pnl') {
        aVal = a.unrealized_pnl || 0;
        bVal = b.unrealized_pnl || 0;
      } else if (sortConfig.key === 'margin_percentage') {
        aVal = a.margin_percentage || 0;
        bVal = b.margin_percentage || 0;
      } else if (sortConfig.key === 'margin_used') {
        aVal = a.margin_used || 0;
        bVal = b.margin_used || 0;
      } else if (sortConfig.key === 'notional') {
        aVal = a.notional || 0;
        bVal = b.notional || 0;
      } else if (sortConfig.key === 'leverage') {
        aVal = a.leverage || 0;
        bVal = b.leverage || 0;
      } else if (sortConfig.key === 'adl') {
        aVal = a.adl || 999; // Put positions without ADL at the end
        bVal = b.adl || 999;
      } else if (sortConfig.key === 'gain_24h_percent') {
        aVal = a.gain_24h_percent ?? -999; // Put positions without 24h gain at the end
        bVal = b.gain_24h_percent ?? -999;
      }
      
      // Handle numeric comparisons
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      // Handle string comparisons
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  };

  filteredPositions = sortPositions(filteredPositions);

  if (allPositions.length === 0) {
    return (
      <div className="positions-table-container">
        <p>No open positions</p>
      </div>
    );
  }

  return (
    <div className="positions-table-container">
      <div className="table-header">
        <h2>All Positions ({filteredPositions.length})</h2>
        <div className="header-controls">
          <div className="header-filters">
            <div className="funding-period-filter">
              <label htmlFor="live-fr-display">Live FR:</label>
              <select
                id="live-fr-display"
                className="period-select"
                value={liveFRDisplay}
                onChange={(e) => setLiveFRDisplay(e.target.value)}
                title="Show live funding rate as raw (per 8h), daily, weekly, or yearly"
              >
                <option value="raw">Raw</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>
            <div className="hedge-filter">
              <label>Hedged:</label>
              <div className="hedge-filter-buttons">
                <button
                  className={`hedge-filter-btn ${hedgeFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setHedgeFilter('all')}
                  title="Show all positions"
                >
                  All
                </button>
                <button
                  className={`hedge-filter-btn yes-btn ${hedgeFilter === 'yes' ? 'active' : ''}`}
                  onClick={() => setHedgeFilter('yes')}
                  title="Show only hedged positions"
                >
                  Yes
                </button>
                <button
                  className={`hedge-filter-btn no-btn ${hedgeFilter === 'no' ? 'active' : ''}`}
                  onClick={() => setHedgeFilter('no')}
                  title="Show only unhedged positions"
                >
                  No
                </button>
              </div>
            </div>
          </div>
          <div className="search-container">
            <svg className="search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M14 14L11.1 11.1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <input
              type="text"
              className="search-input"
              placeholder="Search by symbol, exchange..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className="column-visibility-container" ref={columnMenuRef}>
              <button
                className="column-visibility-button"
                onClick={() => setShowColumnMenu(!showColumnMenu)}
                title="Customize columns"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="8" cy="4" r="1.5" fill="currentColor"/>
                  <circle cx="8" cy="8" r="1.5" fill="currentColor"/>
                  <circle cx="8" cy="12" r="1.5" fill="currentColor"/>
                </svg>
              </button>
              {showColumnMenu && (
                <div className="column-menu">
                  <div className="column-menu-header">Customize Columns</div>
                  <div className="column-menu-list">
                    {columns.map(column => (
                      <label key={column.key} className="column-menu-item">
                        <input
                          type="checkbox"
                          checked={columnVisibility[column.key]}
                          onChange={() => toggleColumn(column.key)}
                        />
                        <span>{column.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="table-wrapper">
        <table className="positions-table">
          <thead>
            <tr>
              {columnVisibility.symbol && (
                <th onClick={() => handleSort('pair')} className="sortable-header">
                  Symbol {sortConfig.key === 'pair' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
              )}
              {columnVisibility.exchange && (
                <th onClick={() => handleSort('account')} className="sortable-header">
                  Exchange {sortConfig.key === 'account' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
              )}
              {columnVisibility.side && (
                <th onClick={() => handleSort('side')} className="sortable-header">
                  Side {sortConfig.key === 'side' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
              )}
              {columnVisibility.quantity && (
                <th onClick={() => handleSort('size')} className="sortable-header">
                  Quantity {sortConfig.key === 'size' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
              )}
              {columnVisibility.avgOpen && (
                <th onClick={() => handleSort('entry_price')} className="sortable-header">
                  Avg. Open {sortConfig.key === 'entry_price' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
              )}
              {columnVisibility.markPrice && (
                <th onClick={() => handleSort('mark_price')} className="sortable-header">
                  Mark Price {sortConfig.key === 'mark_price' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
              )}
              {columnVisibility.gain24h && (
                <th onClick={() => handleSort('gain_24h_percent')} className="sortable-header">
                  24h Gain {sortConfig.key === 'gain_24h_percent' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
              )}
              {columnVisibility.liqPrice && (
                <th onClick={() => handleSort('liquidation_price')} className="sortable-header">
                  Liq. Price {sortConfig.key === 'liquidation_price' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
              )}
              {columnVisibility.adl && (
                <th onClick={() => handleSort('adl')} className="sortable-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    ADL {sortConfig.key === 'adl' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    <ADLTooltip />
                  </div>
                </th>
              )}
              {columnVisibility.unrealizedPnl && (
                <th onClick={() => handleSort('unrealized_pnl')} className="sortable-header">
                  U-PnL (ROE%) {sortConfig.key === 'unrealized_pnl' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
              )}
              {columnVisibility.notional && (
                <th onClick={() => handleSort('notional')} className="sortable-header">
                  Notional {sortConfig.key === 'notional' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
              )}
              {columnVisibility.margin && (
                <th onClick={() => handleSort('margin_percentage')} className="sortable-header">
                  Margin {sortConfig.key === 'margin_percentage' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
              )}
              {columnVisibility.leverage && (
                <th onClick={() => handleSort('leverage')} className="sortable-header">
                  Leverage {sortConfig.key === 'leverage' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
              )}
              {columnVisibility.liveFR && <th title="Live funding rate: raw (8h), daily, weekly, or yearly">Live FR</th>}
              {columnVisibility.funding && <th>Funding</th>}
              {columnVisibility.fundingPct && <th title="% since opening and annualised">Funding %</th>}
              {columnVisibility.countdown && <th>Countdown</th>}
              {columnVisibility.hedging && <th>Hedged</th>}
            </tr>
          </thead>
          <tbody>
            {filteredPositions.length === 0 ? (
              <tr>
                <td colSpan={columns.filter(c => columnVisibility[c.key]).length} className="no-results">
                  No positions found matching "{searchQuery}"
                </td>
              </tr>
            ) : (
              filteredPositions.map((position, idx) => {
              const hedgingInfo = getHedgingInfo(position);
              const pnl = position.unrealized_pnl || 0;
              const pnlPercent = position.margin_used > 0 
                ? (pnl / position.margin_used * 100) 
                : 0;
              const isProfit = pnl >= 0;
              
              return (
                <tr key={`${position.exchange}-${position.pair}-${idx}`} className="position-row">
                  {columnVisibility.symbol && (
                    <td className="symbol-cell">
                      <div className="symbol-info">
                        <span className={`side-indicator ${position.side.toLowerCase()}`}></span>
                        <span className="symbol-name">{position.pair}</span>
                        <span className="leverage-badge">{position.leverage}x</span>
                      </div>
                    </td>
                  )}
                  {columnVisibility.exchange && (
                    <td className="exchange-cell">{position.account}</td>
                  )}
                  {columnVisibility.side && (
                    <td className={`side-cell ${position.side.toLowerCase()}`}>
                      <span className="side-cell-with-bar">
                        <span className="side-bar" aria-hidden="true"></span>
                        <span>{position.side}</span>
                      </span>
                    </td>
                  )}
                  {columnVisibility.quantity && (
                    <td className={`quantity-cell ${position.side.toLowerCase()}`}>
                      {formatLargeNumber(position.size)}
                    </td>
                  )}
                  {columnVisibility.avgOpen && (
                    <td className="price-cell">${formatPrice(position.entry_price)}</td>
                  )}
                  {columnVisibility.markPrice && (
                    <td className="price-cell">${formatPrice(position.mark_price)}</td>
                  )}
                  {columnVisibility.gain24h && (
                    <td className="gain-24h-cell">
                      {position.gain_24h_percent !== null && position.gain_24h_percent !== undefined 
                        ? `${position.gain_24h_percent >= 0 ? '+' : ''}${formatNumber(position.gain_24h_percent, 2)}%`
                        : '-'}
                    </td>
                  )}
                  {columnVisibility.liqPrice && (
                    <td className="price-cell">
                      {position.liquidation_price ? `$${formatPrice(position.liquidation_price)}` : '-'}
                    </td>
                  )}
                  {columnVisibility.adl && (
                    <td className="adl-cell">
                      {position.adl !== null && position.adl !== undefined ? `${position.adl}/5` : '-'}
                    </td>
                  )}
                  {columnVisibility.unrealizedPnl && (
                    <td className={`pnl-cell ${isProfit ? 'profit' : 'loss'}`}>
                      <div className="pnl-value">
                        {isProfit ? '+' : ''}${formatNumber(pnl)}
                      </div>
                      <div className={`pnl-percent ${pnlPercent >= 0 ? 'profit' : 'loss'}`}>
                        ({pnlPercent >= 0 ? '+' : ''}{formatNumber(pnlPercent)}%)
                      </div>
                    </td>
                  )}
                  {columnVisibility.notional && (
                    <td className="notional-cell">
                      {position.notional ? `$${formatNumber(position.notional)}` : '-'}
                    </td>
                  )}
                  {columnVisibility.margin && (
                    <td className="margin-cell">
                      <div>${formatNumber(position.margin_used)}</div>
                      <div className="margin-percent">({formatNumber(position.margin_percentage)}%)</div>
                    </td>
                  )}
                  {columnVisibility.leverage && (
                    <td className="leverage-cell">{position.leverage}x</td>
                  )}
                  {columnVisibility.liveFR && (
                    <td className="live-fr-cell">
                      {formatFundingRate(position)}
                    </td>
                  )}
                  {columnVisibility.funding && (
                    <td className="funding-cell">
                      {formatFundingPayout(position)}
                    </td>
                  )}
                  {columnVisibility.fundingPct && (
                    <td className="funding-pct-cell-wrap">
                      {formatFundingPct(position)}
                    </td>
                  )}
                  {columnVisibility.countdown && (
                    <td className="countdown-cell">
                      {formatCountdown(position)}
                    </td>
                  )}
                  {columnVisibility.hedging && (
                    <td className="hedging-cell">
                      {hedgingInfo ? (
                        hedgingInfo.status === 'fully_hedged' || hedgingInfo.status === 'partially_hedged' ? (
                          <span className="hedge-status yes">Yes</span>
                        ) : (
                          <span className="hedge-status no">No</span>
                        )
                      ) : (
                        <span className="hedge-status no">No</span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default PositionsTable;

